/**
 * @module components/PackageButton
 * @description Styled button component for package actions (download, regenerate, copy password).
 * Supports different visual tones, icons, and disabled states.
 */

import React, { useCallback } from 'react';
import { Sanity } from 'src/types/sanity';
import * as Styles from './styles';
import { PackageButtonProps } from './types';

/**
 * @component PackageButton
 * @summary Styled action button for package operations with icon and tone support.
 * Used for download, regenerate, and password copy actions on order packages.
 * @type {React.FC<PackageButtonProps>}
 * @param {PackageButtonProps} props - Component props from types.ts
 * @returns {React.ReactElement} Styled button with optional icon
 */
export const PackageButton: React.FC<PackageButtonProps> = ({
  children,
  onClick,
  tone = Sanity.Types.SanityTone.DEFAULT,
  disabled = false,
  icon,
  ...rest
}) => {
  const handleClick = useCallback(async () => {
    if (!onClick) return;
    await onClick();
  }, [onClick]);

  return (
    <Styles.Button
      $tone={tone}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      {icon && <Styles.ButtonIcon>{icon}</Styles.ButtonIcon>}
      <Styles.ButtonText>{children}</Styles.ButtonText>
    </Styles.Button>
  );
};

export default PackageButton;
